import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { getLocalizedText } from '@/lib/utils';
import type { LocalizedText } from '@fame/shared';
import { createProjectSchema, type ProjectFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function EditProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id!);
  const updateProject = useUpdateProject();

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(createProjectSchema) as Resolver<ProjectFormData>,
    defaultValues: {
      name: { nl: '', fr: '', en: '' },
      depositArticleNumber: '',
      singleRoomArticleNumber: '',
      balanceArticleNumber: '',
      activityArticleNumber: '',
      activityDescription: { nl: '', fr: '', en: '' },
      defaultCostUnit: '',
      depositAmount: '',
      mollieProfile: 'nocc',
      isActive: true,
    },
  });

  useEffect(() => {
    if (project) {
      form.reset({
        name: project.name,
        depositArticleNumber: project.depositArticleNumber,
        singleRoomArticleNumber: project.singleRoomArticleNumber,
        balanceArticleNumber: project.balanceArticleNumber,
        activityArticleNumber: project.activityArticleNumber ?? '',
        activityDescription: (project.activityDescription as LocalizedText) ?? { nl: '', fr: '', en: '' },
        defaultCostUnit: project.defaultCostUnit ?? '',
        depositAmount: project.depositAmount ?? '',
        mollieProfile: project.mollieProfile ?? 'nocc',
        isActive: project.isActive,
      });
    }
  }, [project, form]);

  const onSubmit = async (data: ProjectFormData) => {
    await updateProject.mutateAsync({ id: id!, data });
    navigate(`/projecten/${id}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Project niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/projecten">Terug naar projecten</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Project Bewerken</h1>
          <p className="text-muted-foreground">{getLocalizedText(project.name)}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project Informatie</CardTitle>
          <CardDescription>Bewerk de basisinformatie en financiële instellingen</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Projectnaam</FormLabel>
                    <FormControl>
                      <MultiLanguageInput placeholder="bijv. Japan 2026" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium">Facturatie</h3>
                  <p className="text-sm text-muted-foreground">Artikelnummers, kostendrager en voorschotinstellingen</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="depositArticleNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Artikelnummer (voorschotfactuur)</FormLabel>
                        <FormControl>
                          <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT0" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="depositAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bedrag voorschot (pp)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="0,00" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormDescription>Het bedrag voor het voorschot per persoon</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="singleRoomArticleNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Artikelnummer (single kamer)</FormLabel>
                        <FormControl>
                          <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT1" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="balanceArticleNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Artikelnummer (saldo factuur)</FormLabel>
                        <FormControl>
                          <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT2" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="activityArticleNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Artikelnummer (activiteiten)</FormLabel>
                        <FormControl>
                          <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT3" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="activityDescription"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Activiteiten omschrijving (voor factuur)</FormLabel>
                        <FormControl>
                          <MultiLanguageInput placeholder="bijv. Optionele activiteiten" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="defaultCostUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kostendrager</FormLabel>
                        <FormControl>
                          <Input placeholder="Optioneel" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mollieProfile"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mollie betaalprofiel</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="nocc">Mollie - excl. creditcard</SelectItem>
                            <SelectItem value="cc">Mollie - incl. creditcard</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actief</FormLabel>
                      <FormDescription>Is dit project momenteel actief?</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${id}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={updateProject.isPending}>
                  {updateProject.isPending ? 'Bezig met opslaan...' : 'Opslaan'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
